import Modal from 'LuxKit/Modal/Modal'
import { useCallback, useMemo, useState } from 'react'
import type { PhonePrefixCountry } from './PhoneInput.types'
import PhonePrefixItem from './PhonePrefixItem'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import SearchFormModalTextField from 'components/SearchV2/Components/SearchFormField/SearchFormModalTextField'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import BodyText from 'LuxKit/Typography/BodyText'
import { useIntl } from 'react-intl'

interface Props {
  countries: Array<PhonePrefixCountry>
  selectedCountryCode?: string
}

function PhoneInputPrefixModal(props: Props) {
  const { countries, selectedCountryCode } = props
  const intl = useIntl()

  const modalContext = useModalElementContext<PhonePrefixCountry | undefined>()
  const [search, setSearch] = useState<string>('')
  const onSelect = useCallback((prefix: PhonePrefixCountry) => {
    modalContext.resolve(prefix)
  }, [modalContext])

  const countryDisplayNames = useMemo(
    () => new Intl.DisplayNames(intl.locale, { type: 'region' }),
    [intl.locale],
  )

  const hiddenPrefixes = useMemo(() => {
    if (search) {
      const sanitizedPrefix = search.replace('+', '').toLowerCase()
      const hiddenCountries = countries.filter(option => {
        const localizedName = countryDisplayNames.of(option.iso2.toUpperCase()) ?? option.name
        return !localizedName.toLowerCase().includes(sanitizedPrefix) &&
          !option.name.toLowerCase().includes(sanitizedPrefix) &&
          !option.dialCode.startsWith(sanitizedPrefix)
      })
      return new Set(hiddenCountries)
    }
    return new Set()
  }, [countries, countryDisplayNames, search])

  return <Modal
    title={intl.formatMessage({
      id: 'zkFkcAdhXKCNnEcpsY_v',
      defaultMessage: 'Country code',
      description: 'Title for phone country code selection modal dialog window',
    })}
    height="max"
  >
    <VerticalSpacer gap={24}>
      <SearchFormModalTextField
        onChange={setSearch}
        placeholder={intl.formatMessage({
          id: 'ME06bQLxIjRTa6svBMp-',
          defaultMessage: 'Search by country or code',
          description: 'Placeholder for phone country code search input on the form',
        })}
        value={search}
        onClear={() => setSearch('')}
        autoFocus
        autoSelect
      />
      <div>
        {hiddenPrefixes.size === countries.length && <BodyText variant="medium">{intl.formatMessage({
          id: 'AKo6awDS665mn3JKE2ew',
          defaultMessage: 'No matching dial codes',
          description: 'Empty state text when no phone prefix matches the search',
        })}
        </BodyText>}
        {countries.map(country => (
          <PhonePrefixItem
            key={country.iso2}
            selected={selectedCountryCode === country.iso2.toLocaleUpperCase()}
            country={country}
            onSelect={onSelect}
            hidden={hiddenPrefixes.has(country)}
          />
        ))}
      </div>
    </VerticalSpacer>
  </Modal>
}

export default PhoneInputPrefixModal
